import React, { useEffect, useState } from "react";
import {
  getMembers,
  addMember,
  updateMember,
  deleteMember,
  addChildAPI,
  addParentAPI
} from "./api/members";
import FamilyTreeView from "./components/FamilyTreeView";
import "./Home.css";
import { useNavigate, Navigate } from "react-router-dom";
import EditMemberModal from "./components/EditMemberModal";
import supabase from "./supabase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowUp } from "@fortawesome/free-solid-svg-icons";
import { faCircleArrowDown } from "@fortawesome/free-solid-svg-icons";
import { logEvent } from "./utils/logEvent";

export default function Home() {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showLinkParent, setShowLinkParent] = useState(false);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function loadUser() {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData?.user) return navigate("/");

      const userId = authData.user.id;
      const { data: userData } = await supabase
        .from("users")
        .select("full_name")
        .eq("user_id", userId)
        .maybeSingle();

      setUser({
        ...authData.user,
        full_name: userData?.full_name || "User"
      });
    }
    loadUser();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  // LOAD MEMBERS
  useEffect(() => {
    if (!user) return;

    async function load() {
      const data = await getMembers();
      const normalized = data.map(m => ({
        ...m,
        parents: Array.isArray(m.parents) ? m.parents : [],
        children: Array.isArray(m.children) ? m.children : []
      }));

      setMembers(normalized);

      if (!selected && normalized.length > 0) {
        const root = normalized.find(m => m.parents.length === 0) || normalized[0];
        setSelected(root);
      }
    }
    load();
  }, [user]);

  // ACTION HANDLERS
  async function handleAddChild() {
    if (!selected) return;

    await logEvent("add_child_intent", { selected_id: selected.id, selected_name: selected.name });

    const name = prompt("Enter child's name:");
    if (!name) return;

    const child = await addMember({ name, parents: [], children: [] });
    await addChildAPI(selected.id, child.id);

    await logEvent(`add_child_executed`, {
      selected_id: selected.id,
      selected_name: selected.name,
      child_id: child.id,
      child_name: child.name
    });

    setMembers(await getMembers());
  }

  async function handleAddParent() {
    if (!selected) return;

    await logEvent("add_parent_intent", { selected_id: selected.id, selected_name: selected.name });

    const name = prompt("Enter parent's name:");
    if (!name) return;

    const parent = await addMember({ name, parents: [], children: [] });
    await addParentAPI(selected.id, parent.id);

    await logEvent(`add_parent_executed`, {
      selected_id: selected.id,
      selected_name: selected.name,
      parent_id: parent.id,
      parent_name: parent.name
    });

    setMembers(await getMembers());
  }
  
  async function handleViewProfile() {
    if (selected) navigate(`/member/${selected.id}`);

    await logEvent("view_profile_executed_side_menu", {
      selected_id: selected.id,
      selected_name: selected.name
    });
  }

  async function handleEditName() {
    if (!selected) return;

    await logEvent("edit_name_intent", { selected_id: selected.id, selected_name: selected.name });
  }

  async function handleLinkParent() {
    if (!selected) return;

    await logEvent("link_parent_intent", { selected_id: selected.id, selected_name: selected.name });
  }

  return (
    <div className="home-root">

      {/* 🔵 TOP BAR */}
      <div className="top-bar">
        <div className="top-left">Welcome, {user?.full_name}!</div>
        <div className="top-center">Family Story Tree</div>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <div className="home-content">

        {/* 🔵 LEFT PANEL */}
        <div className="side-panel">
          <h2 className="side-title">Selected Member</h2>

          {selected ? (
            <>
              <p className="selected-name">{selected.name}</p>

              <div className="button-stack">
                <button onClick={handleAddParent}><FontAwesomeIcon icon={faCircleArrowUp} /> Add a parent</button>
                <button onClick={handleAddChild}><FontAwesomeIcon icon={faCircleArrowDown} />  Add a child</button>
                <button onClick={() => { handleEditName(); setIsEditing(true) } }>Edit name</button>
                <button onClick={() => { handleLinkParent(); setShowLinkParent(true) }}>Link a parent</button>

                <button onClick={handleViewProfile}>View profile</button>
                <button
                  className="danger-btn"
                  onClick={async () => {
                    if (!selected) return;

                    logEvent("delete_member_intent", {
                      selected_id: selected.id,
                      selected_name: selected.name
                    });

                    if (!window.confirm("Delete this member?")) return;
                    await deleteMember(selected.id);

                    logEvent("delete_member_executed", {
                      selected_id: selected.id,
                      selected_name: selected.name
                    });

                    setMembers(await getMembers());
                    setSelected(null);
                  }}
                >
                  Delete
                </button>
              </div>
            </>
          ) : (
            <p>No member selected.</p>
          )}

          {isEditing && (
            <EditMemberModal
              member={selected}
              onClose={() => setIsEditing(false)}
              onSave={async updated => {
                await updateMember(updated.id, updated);

                await logEvent("edit_name_executed", {
                  selected_id: selected.id,
                  selected_name: selected.name,
                  new_name: updated.name
                });

                const refreshed = await getMembers();
                setMembers(refreshed);
                setSelected(refreshed.find(m => m.id === updated.id));
                setIsEditing(false);
              }}
            />
          )}
          {showLinkParent && (
  <div className="overlay">
    <div className="modal">
      <h3>Select Parent</h3>
      {members
        .filter(m => m.id !== selected.id)
        .map(m => (
          <button
            key={m.id}
            onClick={async () => {
              await addParentAPI(selected.id, m.id);

              await logEvent("link_parent_executed", {
                selected_id: selected.id,
                selected_name: selected.name,
                parent_id: m.id,
                parent_name: m.name
              });

              const refreshed = await getMembers();
              setMembers(refreshed);
              setShowLinkParent(false);
            }}
          >
            {m.name}
          </button>
        ))}

      <button className="close" onClick={() => setShowLinkParent(false)}>
        Close
      </button>
    </div>
  </div>
)}

        </div>
        

        {/* 🔵 TREE + ZOOM UI */}
        <div className="tree-panel">
          <div className="tree-controls">
            <button onClick={() => window.dispatchEvent(new Event("zoom_in"))}>+</button>
            <button onClick={() => window.dispatchEvent(new Event("zoom_out"))}>-</button>
            <button onClick={() => window.dispatchEvent(new Event("pan_right"))}>←</button>
            <button onClick={() => window.dispatchEvent(new Event("pan_left"))}>→</button>
            <button onClick={() => window.dispatchEvent(new Event("pan_down"))}>↑</button>
            <button onClick={() => window.dispatchEvent(new Event("pan_up"))}>↓</button>
          </div>

          <FamilyTreeView
            members={members}
            onSelect={setSelected}
            selectedNodeId={selected?.id}
          />
        </div>
      </div>
    </div>
  );
}
